import React from "react";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Button, Grid, MenuItem } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import { CompanyType } from "@inosio/payments-sdk";
import { useDispatch, useSelector } from "react-redux";
import { InputField } from "../../../components/InputController";
import { useStyles } from "../../../style/Pages/BasicCompanyInfo";
import { schemaValidationLegalCompanyInfo, schemaValidationLegalCompany } from "../../../utils/validation";
import { selectOnboardingApplicationState, updateOnboardingDto } from "../sagas/pmonboardingSlice";
import CustomDropdown from "../../../components/customDropdown";
import { removeDashes } from "../../shared/util/helpers";
import { approvedISOStandards } from "../../../shared/util/approvedISOStandards";
import { openAccordions } from "../ApplicationDashboard";

interface IState {
  name: CompanyType;
}
export const useStyle = makeStyles(() => ({
  accordionStyle: {
    borderRadius: "24px !important",
    overflow: "hidden",
  },
}));

const textStyle = {
  fontWeight: "bold",
};

interface FormLegalInfoPropType {
  openAccordion?: string | boolean;
  setOpenAccordion?: React.Dispatch<React.SetStateAction<openAccordions | false>>;
}

export default function FormLegalInfo(props: FormLegalInfoPropType) {
  const selectOnboardingState = useSelector(selectOnboardingApplicationState);
  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;
  const dispatch = useDispatch();
  //Legal Company Info Saved Values
  const [savedValues, setSavedValues] = React.useState<schemaValidationLegalCompanyInfo>({
    company_legal_name: "",
    company_DBA_name: "",
    company_mailing_address: "",
    city: "",
    state: approvedStates[0].Code,
    zip: "",
    country: "US",
    state_of_incorporation: "",
    tax_ID: "",
    company_type: "",
  });

  React.useEffect(() => {
    selectOnboardingState.onboarding?.DbaName &&
      setSavedValues({
        ...savedValues,
        company_legal_name: selectOnboardingState.onboarding?.LegalName!,
        company_DBA_name: selectOnboardingState.onboarding?.DbaName!,
        company_mailing_address: selectOnboardingState.onboarding?.MailingAddress?.Line1!,
        city: selectOnboardingState.onboarding?.MailingAddress?.City!,
        state: selectOnboardingState.onboarding?.MailingAddress?.State!,
        zip: selectOnboardingState.onboarding?.MailingAddress?.Zip!,
        country: selectOnboardingState.onboarding?.MailingAddress?.Country!,
        state_of_incorporation: selectOnboardingState.onboarding?.IncorporationState!,
        tax_ID: selectOnboardingState.onboarding?.TaxId!,
        company_type: selectOnboardingState.onboarding?.CompanyType!,
      });
  }, [selectOnboardingState.onboarding]);

  const formik = useFormik<schemaValidationLegalCompanyInfo>({
    initialValues: savedValues,
    validationSchema: schemaValidationLegalCompany,
    onSubmit: async (values) => {
      dispatch({
        type: updateOnboardingDto.type,
        payload: {
          IncorporationState: values.state_of_incorporation,
          TaxId: removeDashes(values.tax_ID),
          CompanyType: values.company_type,
          MailingAddress: {
            City: values.city,
            State: values.state,
            Zip: values.zip,
            Line1: values.company_mailing_address,
            Country: values.country,
          },
          DbaName: values.company_DBA_name,
          LegalName: values.company_legal_name,
        },
      });
    },
    enableReinitialize: true,
  });

  const [selectedState, setSelectedState] = React.useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    formik.setFieldValue("state", "");
    formik.setFieldValue("state_of_incorporation", "");
    setSelectedState("");
  };

  // load selected state if a valid selection was made originally, else let plain text box render state of origin
  React.useEffect(() => {
    formik.values.country && ["US", "CA"].includes(formik.values.country) && approvedStates.map((stateRecord) => stateRecord.Code).includes(formik.values.state) && setSelectedState(formik.values.state);
  }, [selectOnboardingState.onboarding, formik]);

  const classes = useStyle();
  const classB = useStyles();
  const [companies] = React.useState<Array<IState>>([{ name: CompanyType.SoleProprietorship }, { name: CompanyType.Corporation }, { name: CompanyType.Partnership }, { name: CompanyType.Nonprofit }, { name: CompanyType.Llc }]);
  return (
    <Box sx={{ marginTop: "3%" }}>
      <form onSubmit={formik.handleSubmit}>
        <Accordion
          className={classes.accordionStyle}
          expanded={props.openAccordion === openAccordions.legalInfo}
          onChange={(e: React.SyntheticEvent, isExpanded: boolean) => {
            props.setOpenAccordion && props.setOpenAccordion(isExpanded ? openAccordions.legalInfo : false);
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography sx={textStyle}>Legal Company Info</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <InputField
              name={"company_legal_name"}
              placeholder={"Company Legal Name"}
              label={"Company Legal Name"}
              isError={false}
              errors={formik.errors?.company_legal_name}
              touched={formik.touched?.company_legal_name}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("company_legal_name", e.target?.value)}
              value={formik.values.company_legal_name ?? ""}
            />
            <InputField
              name={"company_DBA_name"}
              placeholder={"Company DBA Name"}
              label={"Company DBA Name"}
              isError={false}
              errors={formik.errors?.company_DBA_name}
              touched={formik.touched?.company_DBA_name}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("company_DBA_name", e.target?.value)}
              value={formik.values.company_DBA_name ?? ""}
            />

            <InputField
              name={"company_mailing_address"}
              placeholder={"Company Mailing Address"}
              label={"Company Mailing Address"}
              isError={false}
              errors={formik.errors?.company_mailing_address}
              touched={formik.touched?.company_mailing_address}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("company_mailing_address", e.target?.value)}
              value={formik.values.company_mailing_address ?? ""}
            />
            <Grid container spacing={1} direction="row">
              <Grid item md={6}>
                <InputField
                  name={"city"}
                  placeholder={"City"}
                  label={"City"}
                  isError={false}
                  errors={formik.errors?.city}
                  touched={formik.touched?.city}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("city", e.target?.value)}
                  value={formik.values.city ?? ""}
                />
              </Grid>
              <Grid item md={6}>
                {formik.values.country && ["US", "CA"].includes(formik.values.country) ? (
                  <>
                    <CustomDropdown
                      id={"state-dropdown"}
                      isFullWidth
                      label={"State"}
                      labelId={""}
                      value={selectedState}
                      handleChange={(e) => {
                        setSelectedState(e.target?.value);
                        formik.setFieldValue("state", e.target?.value);
                      }}
                    >
                      {formik.values.country === "US"
                        ? approvedStates.map((stateRecord) => (
                            <MenuItem style={{ height: "36px" }} key={stateRecord.Code} value={stateRecord.Code}>
                              {stateRecord.State + " - " + stateRecord.Code}
                            </MenuItem>
                          ))
                        : approvedProvinces.map((provinceRecord) => (
                            <MenuItem style={{ height: "36px" }} key={provinceRecord.Code} value={provinceRecord.Code}>
                              {provinceRecord.Province + " - " + provinceRecord.Code}
                            </MenuItem>
                          ))}{" "}
                    </CustomDropdown>
                  </>
                ) : (
                  <InputField
                    value={formik.values.state}
                    name={"state"}
                    isError={false}
                    errors={formik.errors?.state}
                    touched={formik.touched?.state}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue("state", e.target?.value)}
                    placeholder={"State"}
                    label={"State"}
                  />
                )}{" "}
              </Grid>
            </Grid>
            <Grid container spacing={1} direction="row">
              <Grid item md={6}>
                <InputField
                  name={"zip"}
                  placeholder={"zip"}
                  label={"zip"}
                  isError={false}
                  errors={formik.errors?.zip}
                  touched={formik.touched?.zip}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("zip", e.target?.value)}
                  value={formik.values.zip ?? ""}
                />
              </Grid>
              <Grid item md={6}>
                <CustomDropdown
                  label={"Country"}
                  isFullWidth
                  handleChange={(e: any) => {
                    resetStateSelection();

                    // update selected country
                    formik.setFieldValue("country", e.target?.value);
                  }}
                  value={formik.values.country ?? ""}
                >
                  {approvedCountries.map((countryRecord) => (
                    <MenuItem style={{ height: "36px" }} key={countryRecord.Code} value={countryRecord.Code}>
                      {countryRecord.Country + " - " + countryRecord.Code}
                    </MenuItem>
                  ))}
                </CustomDropdown>
              </Grid>
            </Grid>
            <CustomDropdown isFullWidth label={"State of Incorporation"} value={formik.values.state_of_incorporation} handleChange={(e: any) => formik.setFieldValue("state_of_incorporation", e?.target?.value)}>
              {formik.values.country === "US" ? (
                approvedStates.map((stateRecord, index) => (
                  <MenuItem style={{ height: "36px" }} key={index} value={stateRecord.Code}>
                    {stateRecord.State + " - " + stateRecord.Code}
                  </MenuItem>
                ))
              ) : (
                <MenuItem style={{ height: "36px" }} value="NA">
                  other
                </MenuItem>
              )}{" "}
            </CustomDropdown>
            <InputField
              name={"tax_ID"}
              placeholder={"Tax ID"}
              type="tax_ID"
              label={"Tax Id"}
              isError={false}
              errors={formik.errors?.tax_ID}
              touched={formik.touched?.tax_ID}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("tax_ID", e.target?.value)}
              value={formik.values.tax_ID ?? ""}
            />
            <CustomDropdown label={"Company Type"} isFullWidth handleChange={(e) => formik.setFieldValue("company_type", e.target?.value)} value={formik.values.company_type}>
              {companies.map((companyType, index) => (
                <MenuItem style={{ height: "36px" }} key={index} value={companyType.name}>
                  {companyType.name}
                </MenuItem>
              ))}
            </CustomDropdown>
            <Box display="flex" justifyContent="end">
              <Button type="submit" disabled={Boolean(!formik.isValid || !formik.dirty)} className={classB.signUpBtn}>
                <Typography className={classB.signUpBtnText}>update</Typography>
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </form>
    </Box>
  );
}
